import EastIcon from "@mui/icons-material/East"
import { Stack, Typography } from "@mui/material"
import router from "next/router"
import { FC, Fragment, useMemo, useState } from "react"

import { getPath } from "../../../../helpers/paths"
import { useAccount } from "../../../../states/account"
import { Button } from "src/components/common/button"
import { create } from "src/helpers/bem"
import { deepClone } from "src/helpers/deepClone"
import { useTranslation } from "src/hooks/translation"

import { getFormById } from "src/queriesXRM/form"
import {
  BookingDetails,
  mergeBookingDetail,
  useBooking,
} from "src/states/bookingDetail"
import { mergeBookingDetailPermissions } from "src/states/bookingDetailPermissions"
import { useBookings } from "src/states/bookings"
import { mergeCoExhibitorInvite } from "src/states/coExhibitorInvite"
import { useLocale } from "src/states/locale"

import { resetSalesorder, setSalesorderSteps } from "src/states/salesorder"
import { SharedPageProps } from "src/types/SharedPageProps"
import { xRMApiStep } from "src/types/xRM"

import styles from "./BookingDetailOverview.module.scss"

const bem = create(styles, "BookingDetailOverview")

export interface BookingDetailOverviewProps extends SharedPageProps {
  bookingId: string
}

export const BookingDetailOverview: FC<BookingDetailOverviewProps> = ({
  bookingId,
  pages,
  messages,
}) => {
  const { messages: translations } = useTranslation()
  const { bookings } = useBookings()
  const { permissions } = useAccount()
  const { booking: bookingDetail, coExhibitors } = useBooking()
  const locale = useLocale()
  const bookingLabels = translations.pages.booking.detail
  const activeBooking = bookings.find((booking) => booking.id === bookingId)
  const hasAdminPermission = permissions?.some(
    (el) =>
      el.salesorderId === activeBooking?.id && el.role === "Administrator",
  )
  const data = [
    {
      label: bookingLabels.exhibition.exhibitionName,
      value: activeBooking?.exhibition ?? "",
    },
    {
      label: bookingLabels.exhibition.exhibitor,
      value: activeBooking?.exhibitor ?? "",
    },
    {
      label: bookingLabels.exhibition.createdOn,
      value: activeBooking?.createdOn ?? "",
    },
    ...(hasAdminPermission
      ? [
          {
            label: bookingLabels.exhibition.coExhibitor,
            value: coExhibitors.length ?? "",
          },
        ]
      : []),
  ]

  const [loading, setLoading] = useState(false)

  const redirectBookingForm = useMemo(
    () =>
      pages?.data?.nodes?.filter(
        (page) =>
          page?.language?.slug === locale &&
          page?.template?.templateName === "Booking Form Template",
      )?.[0],
    [pages, locale],
  )

  const openBookingAsCopy = async () => {
    setLoading(true)
    const formResultAsDraft = await getFormById(
      activeBooking?.id ?? "",
      "Main",
      "Draft",
      process.env.NEXT_PUBLIC_XRM_EVENT_ID,
    )

    const data = JSON.parse(formResultAsDraft?.currentForm) as BookingDetails
    const steps = deepClone<xRMApiStep[]>(data.steps)

    resetSalesorder()
    setSalesorderSteps(steps)

    await router.push(redirectBookingForm?.uri as string).finally(() => {
      setLoading(false)
    })
  }

  return (
    <Stack className={bem("card")} direction={"column"}>
      <Stack
        className={bem("card", "content")}
        direction={"row"}
        justifyContent={"space-between"}
      >
        <Stack direction={"column"}>
          {data.map((item) => (
            <Stack
              key={item.label}
              className={bem("data")}
              direction={"row"}
              justifyContent={"flex-start"}
            >
              <Typography variant={"body1"}>{item.label}:</Typography>
              <Typography variant={"body1"}>{item.value}</Typography>
            </Stack>
          ))}
        </Stack>
        {hasAdminPermission && (
          <Stack direction={"column"} spacing={2}>
            {/* {bookingDetail?.phase && (
            <span className={bem("status", bookingDetail.phase)}>
              {(messages.pages.booking.overview.table.phase.items as any)?.[
                bookingDetail.phase
              ] ?? bookingDetail.phase}
            </span>
          )} */}
            {bookingDetail?.phase === "Draft" ? (
              <Button
                buttonType="primary"
                className={bem("button")}
                endIcon={<EastIcon fontSize="small" />}
                title={bookingLabels.banner.buttonDraft}
                onClick={() => {
                  mergeBookingDetail({ editDraft: true })
                  router.push(getPath(locale, "registration"))
                }}
              />
            ) : (
              <Fragment>
                <Button
                  buttonType="primary"
                  className={bem("button")}
                  endIcon={<EastIcon fontSize="small" />}
                  title={bookingLabels.banner.buttonAddCoExhibitor}
                  onClick={() =>
                    mergeCoExhibitorInvite({
                      open: true,
                    })
                  }
                />
                <Button
                  buttonType="primary"
                  className={bem("button")}
                  endIcon={<EastIcon />}
                  title={bookingLabels.banner.buttonPermission}
                  onClick={() => mergeBookingDetailPermissions({ open: true })}
                />
                <Button
                  buttonType="primary"
                  className={bem("button")}
                  endIcon={<EastIcon />}
                  title={bookingLabels.banner.buttonCopy}
                  onClick={() => openBookingAsCopy()}
                />
              </Fragment>
            )}
          </Stack>
        )}
      </Stack>
    </Stack>
  )
}
